import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Flag, XCircle, Trash2, CheckCircle, Building2, Mail, Phone, Globe,
    MapPin, Award, Users, Settings, FileText, Activity, Save, CreditCard, GraduationCap, Briefcase, Shield, Layers
} from 'lucide-react';
import api from '../api';
import Loader from '../components/Loader';
import StageAllocation from '../components/university/StageAllocation';
import './ProfessorProfileView.css'; // Reuse the same CSS

function UniversityProfileView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [university, setUniversity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('profile');
    const [isUpdating, setIsUpdating] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');
    const [associatedUsers, setAssociatedUsers] = useState({ faculty: [], students: [], tpo: [], roles: [] });
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);

    const fetchAssociatedUsers = useCallback(async () => {
        setLoadingUsers(true);
        try {
            const response = await api.get(`/admin/university/${id}/associated-users`);
            setAssociatedUsers(response.data);
        } catch (err) {
            console.error('Failed to load associated users:', err);
        } finally {
            setLoadingUsers(false);
        }
    }, [id]);

    useEffect(() => {
        if (activeTab === 'faculty' || activeTab === 'students' || activeTab === 'tpo' || activeTab === 'roles') {
            fetchAssociatedUsers();
        }
    }, [activeTab, fetchAssociatedUsers]);

    const fetchUniversityDetails = useCallback(async () => {
        try {
            const response = await api.get(`/admin/university/${id}`);
            setUniversity(response.data);
            setAdminNotes(response.data.admin_notes || '');
        } catch (err) {
            console.error('Failed to load university profile:', err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchUniversityDetails();
    }, [id, fetchUniversityDetails]);

    const handleStatusUpdate = async (statusUpdate) => {
        setIsUpdating(true);
        try {
            await api.put(`/admin/university/${id}/status`, statusUpdate);
            await fetchUniversityDetails();
        } catch (err) {
            console.error('Failed to update status:', err);

        } finally {
            setIsUpdating(false);
        }
    };

    const handleSaveNotes = async () => {
        try {
            await api.post(`/admin/university/${id}/notes`, { notes: adminNotes });
        } catch (err) {
            console.error('Failed to save notes:', err);
        }
    };

    const handleDeactivateAccount = async () => {
        setIsUpdating(true);
        try {
            await api.put(`/admin/university/${id}/deactivate`);
            await fetchUniversityDetails();
            setShowDeleteModal(false);
        } catch (err) {
            console.error('Failed to deactivate account:', err);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleReactivateAccount = async () => {
        setIsUpdating(true);
        try {
            await api.put(`/admin/university/${id}/reactivate`);
            await fetchUniversityDetails();
        } catch (err) {
            console.error('Failed to reactivate account:', err);
        } finally {
            setIsUpdating(false);
        }
    };

    if (loading) return <Loader />;

    if (!university) return (
        <div className="professor-profile-view" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <div style={{ textAlign: 'center' }}>
                <XCircle size={48} color="#ef4444" style={{ margin: '0 auto 1rem' }} />
                <h2>Access Denied</h2>
                <p>Unable to load university profile</p>
                <button onClick={() => navigate('/admin-dashboard')} className="admin-action-btn">
                    <ArrowLeft size={18} /> Back to Dashboard
                </button>
            </div>
        </div>
    );

    return (
        <div className="professor-profile-view">
            {/* Header */}
            <div className="profile-header">
                <div className="header-left">
                    <button className="back-btn" onClick={() => navigate('/admin-dashboard')}>
                        <ArrowLeft size={20} />
                    </button>
                    <div className="profile-intro">
                        <h1>{university.institution_name || 'University Profile'}</h1>
                        <p style={{ margin: '4px 0 8px 0', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                            UUID: <span style={{ fontFamily: 'monospace', userSelect: 'all' }}>{university.uuid || university.id}</span>
                        </p>
                        <div className="profile-badges">
                            {university.status === 'Deleted' ? (
                                <span className="badge inactive">Inactive</span>
                            ) : (
                                <span className={`badge ${university.is_active ? 'active' : 'inactive'}`}>
                                    {university.is_active ? 'Active' : 'Inactive'}
                                </span>
                            )}
                            {university.is_flagged && <span className="badge flagged">Flagged</span>}
                            {university.is_suspended && <span className="badge suspended">Suspended</span>}
                            {university.tier && <span className="badge verified">{university.tier}</span>}
                            <span className="badge" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                                LMS LICENSES: {university.lms_licenses || 0}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="header-actions">
                    <button
                        className={`admin-action-btn ${university.is_flagged ? 'active' : ''}`}
                        onClick={() => handleStatusUpdate({ is_flagged: !university.is_flagged })}
                        disabled={isUpdating}
                    >
                        <Flag size={18} />
                        {university.is_flagged ? 'Unflag' : 'Flag'}
                    </button>
                    <button
                        className={`admin-action-btn ${university.is_suspended ? 'active' : ''}`}
                        onClick={() => handleStatusUpdate({ is_suspended: !university.is_suspended })}
                        disabled={isUpdating}
                    >
                        {university.is_suspended ? <CheckCircle size={18} /> : <XCircle size={18} />}
                        {university.is_suspended ? 'Unsuspend' : 'Suspend'}
                    </button>
                    {university.status === 'Deleted' ? (
                        <button
                            className="admin-action-btn"
                            onClick={handleReactivateAccount}
                            disabled={isUpdating}
                        >
                            <CheckCircle size={18} />
                            Reactivate Account
                        </button>
                    ) : (
                        <button
                            className="admin-action-btn danger"
                            onClick={() => setShowDeleteModal(true)}
                            disabled={isUpdating}
                        >
                            <Trash2 size={18} />
                            Deactivate Account
                        </button>
                    )}
                </div>
            </div>

            {/* Main Grid */}
            <div className="profile-main-grid">
                {/* Sidebar */}
                <div className="profile-sidebar">
                    <nav className="tab-nav">
                        <button className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
                            <Building2 size={20} /> Profile
                        </button>
                        <button className={`nav-item ${activeTab === 'institutional' ? 'active' : ''}`} onClick={() => setActiveTab('institutional')}>
                            <Award size={20} /> Institutional
                        </button>
                        <button className={`nav-item ${activeTab === 'preferences' ? 'active' : ''}`} onClick={() => setActiveTab('preferences')}>
                            <Settings size={20} /> Preferences
                        </button>
                        <button className={`nav-item ${activeTab === 'billing' ? 'active' : ''}`} onClick={() => setActiveTab('billing')}>
                            <FileText size={20} /> Billing
                        </button>
                        <button className={`nav-item ${activeTab === 'bank' ? 'active' : ''}`} onClick={() => setActiveTab('bank')}>
                            <CreditCard size={20} /> Bank Details
                        </button>
                        <button className={`nav-item ${activeTab === 'activity' ? 'active' : ''}`} onClick={() => setActiveTab('activity')}>
                            <Activity size={20} /> Activity
                        </button>
                        <button className={`nav-item ${activeTab === 'faculty' ? 'active' : ''}`} onClick={() => setActiveTab('faculty')}>
                            <Users size={20} /> Faculty
                        </button>
                        <button className={`nav-item ${activeTab === 'students' ? 'active' : ''}`} onClick={() => setActiveTab('students')}>
                            <GraduationCap size={20} /> Students
                        </button>
                        <button className={`nav-item ${activeTab === 'ai_tiers' ? 'active' : ''}`} onClick={() => setActiveTab('ai_tiers')}>
                            <Layers size={20} /> AI Interview Tiers
                        </button>
                        <button className={`nav-item ${activeTab === 'tpo' ? 'active' : ''}`} onClick={() => setActiveTab('tpo')}>
                            <Briefcase size={20} /> TPO
                        </button>
                        <button className={`nav-item ${activeTab === 'roles' ? 'active' : ''}`} onClick={() => setActiveTab('roles')}>
                            <Shield size={20} /> Custom Roles
                        </button>
                    </nav>

                    {/* Admin Notes */}
                    <div className="admin-notes-section">
                        <h3><FileText size={16} /> Admin Notes</h3>
                        <textarea
                            value={adminNotes}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            placeholder="Add internal notes about this university..."
                        />
                        <button className="save-notes-btn" onClick={handleSaveNotes}>
                            <Save size={16} /> Save Notes
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="profile-content">
                    {activeTab === 'profile' && (
                        <>
                            <div className="info-section">
                                <h2>Basic Information</h2>
                                <div className="info-grid">
                                    <div className="info-item">
                                        <label>Institution Name</label>
                                        <p>{university.institution_name || 'Not provided'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Institution Type</label>
                                        <p>{university.institution_type || 'Not provided'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>LMS Institution ID</label>
                                        {university.onboarding_institutions_id ? (
                                            <p style={{ fontFamily: 'monospace', background: 'var(--engine-black)', padding: '4px 8px', borderRadius: '4px', display: 'inline-block', border: '1px solid var(--border)' }}>
                                                {university.onboarding_institutions_id}
                                            </p>
                                        ) : (
                                            <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not synced to LMS</p>
                                        )}
                                    </div>
                                    <div className="info-item full">
                                        <label>Description</label>
                                        <p>{university.description || 'No description provided'}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="info-section">
                                <h2>Contact Details</h2>
                                <div className="info-grid">
                                    <div className="info-item">
                                        <label>Contact Person</label>
                                        <p><Users size={16} /> {university.contact_person || 'Not provided'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Contact Email</label>
                                        <p><Mail size={16} /> {university.contact_email || university.email}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Account Phone</label>
                                        <p><Phone size={16} /> {university.phone_number || 'Not provided'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Contact Phone</label>
                                        <p><Phone size={16} /> {university.contact_phone || 'Not provided'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Website</label>
                                        <p>
                                            <Globe size={16} />
                                            {university.website_url ? (
                                                <a href={university.website_url} target="_blank" rel="noopener noreferrer">
                                                    {university.website_url}
                                                </a>
                                            ) : 'Not provided'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="info-section">
                                <h2>Location & Address</h2>
                                <div className="info-grid">
                                    <div className="info-item full">
                                        <label>Address Line 1</label>
                                        <p><MapPin size={16} /> {university.address_line1 || 'Not provided'}</p>
                                    </div>
                                    {university.address_line2 && (
                                        <div className="info-item full">
                                            <label>Address Line 2</label>
                                            <p>{university.address_line2}</p>
                                        </div>
                                    )}
                                    <div className="info-item">
                                        <label>City</label>
                                        <p>{university.city || 'Not provided'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>State</label>
                                        <p>{university.state || 'Not provided'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Country</label>
                                        <p>{university.country || 'Not provided'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Postal Code</label>
                                        <p>{university.postal_code || 'Not provided'}</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === 'institutional' && (
                        <div className="info-section">
                            <h2>Institutional Details</h2>
                            <div className="info-grid">
                                <div className="info-item">
                                    <label>Accreditation</label>
                                    <p><Award size={16} /> {university.accreditation || 'Not provided'}</p>
                                </div>
                                <div className="info-item">
                                    <label>Establishment Year</label>
                                    <p>{university.establishment_year || 'Not provided'}</p>
                                </div>
                                <div className="info-item">
                                    <label>Student Strength</label>
                                    <p><Users size={16} /> {university.student_strength ? university.student_strength.toLocaleString() : 'Not provided'}</p>
                                </div>
                                <div className="info-item">
                                    <label>Tier</label>
                                    <p>{university.tier || 'Not assigned'}</p>
                                </div>
                                <div className="info-item">
                                    <label>Profile Completion</label>
                                    <p>{university.profile_completion_percentage}%</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'preferences' && (
                        <div className="info-section">
                            <h2>Preferences & Settings</h2>
                            <div className="info-grid">
                                <div className="info-item full">
                                    <label>Preferred Domains</label>
                                    <div className="skills-cloud">
                                        {university.preferred_domains && university.preferred_domains.length > 0 ? (
                                            university.preferred_domains.map((domain, idx) => (
                                                <span key={idx} className="skill-tag">{domain}</span>
                                            ))
                                        ) : (
                                            <p>No preferred domains set</p>
                                        )}
                                    </div>
                                </div>
                                <div className="info-item">
                                    <label>Email Notifications</label>
                                    <p style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{university.notification_email_enabled ? <><CheckCircle size={16} color="#10b981" /> Enabled</> : <><XCircle size={16} color="#ef4444" /> Disabled</>}</p>
                                </div>
                                <div className="info-item">
                                    <label>SMS Notifications</label>
                                    <p style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{university.notification_sms_enabled ? <><CheckCircle size={16} color="#10b981" /> Enabled</> : <><XCircle size={16} color="#ef4444" /> Disabled</>}</p>
                                </div>
                                <div className="info-item">
                                    <label>Session Approval Mode</label>
                                    <p>{university.session_approval_mode}</p>
                                </div>
                                <div className="info-item">
                                    <label>Profile Visibility</label>
                                    <p>{university.profile_visibility}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'billing' && (
                        <div className="info-section">
                            <h2>Billing Address</h2>
                            <div className="info-grid">
                                <div className="info-item full">
                                    <label>Same as Institution Address</label>
                                    <p style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{university.billing_address_same ? <><CheckCircle size={16} color="#10b981" /> Yes</> : <><XCircle size={16} color="#ef4444" /> No</>}</p>
                                </div>
                                {!university.billing_address_same && (
                                    <>
                                        <div className="info-item full">
                                            <label>Billing Address Line 1</label>
                                            <p>{university.billing_address_line1 || 'Not provided'}</p>
                                        </div>
                                        {university.billing_address_line2 && (
                                            <div className="info-item full">
                                                <label>Billing Address Line 2</label>
                                                <p>{university.billing_address_line2}</p>
                                            </div>
                                        )}
                                        <div className="info-item">
                                            <label>Billing City</label>
                                            <p>{university.billing_city || 'Not provided'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Billing State</label>
                                            <p>{university.billing_state || 'Not provided'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Billing Country</label>
                                            <p>{university.billing_country || 'Not provided'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Billing Postal Code</label>
                                            <p>{university.billing_postal_code || 'Not provided'}</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'bank' && (
                        <div className="info-section">
                            <h2>Bank Details</h2>
                            {university.bank_details ? (
                                <div className="info-grid">
                                    <div className="info-item">
                                        <label>Account Holder Name</label>
                                        <p>{university.bank_details.account_holder_name}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Bank Name</label>
                                        <p>{university.bank_details.bank_name}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Account Number</label>
                                        <p style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>
                                            {university.bank_details.account_number}
                                        </p>
                                    </div>
                                    <div className="info-item">
                                        <label>IFSC Code</label>
                                        <p style={{ fontFamily: 'monospace' }}>{university.bank_details.ifsc_code}</p>
                                    </div>
                                    {university.bank_details.branch_name && (
                                        <div className="info-item">
                                            <label>Branch Name</label>
                                            <p>{university.bank_details.branch_name}</p>
                                        </div>
                                    )}
                                    {university.bank_details.account_type && (
                                        <div className="info-item">
                                            <label>Account Type</label>
                                            <p>{university.bank_details.account_type}</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '3rem', background: 'var(--engine-black)', borderRadius: '12px', border: '2px dashed var(--border)' }}>
                                    <CreditCard size={48} color="var(--text-faint)" style={{ marginBottom: '1rem' }} />
                                    <p style={{ color: 'var(--text-muted)' }}>No bank details linked to this university.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'activity' && (
                        <div className="info-section">
                            <h2>Activity Overview</h2>
                            <div className="info-grid">
                                <div className="info-item">
                                    <label>Account Created</label>
                                    <p>{new Date(university.created_at).toLocaleDateString()}</p>
                                </div>
                                <div className="info-item">
                                    <label>Account Email</label>
                                    <p><Mail size={16} /> {university.email}</p>
                                </div>
                            </div>
                            <p className="text-muted" style={{ marginTop: '2rem' }}>
                                Additional activity metrics (requirements posted, experts engaged, etc.) will be displayed here.
                            </p>
                        </div>
                    )}

                    {activeTab === 'faculty' && (
                        <div className="info-section">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <h2>Registered Faculty</h2>
                                <span className="badge verified">{associatedUsers.faculty.length} Members</span>
                            </div>
                            {loadingUsers ? (
                                <div style={{ textAlign: 'center', padding: '2rem' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
                            ) : associatedUsers.faculty.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '3rem', border: '1px dashed var(--border)', borderRadius: '12px', background: 'rgba(255,255,255,0.02)' }}>
                                    <Users size={40} style={{ color: 'var(--text-muted)', marginBottom: '1rem' }} />
                                    <p style={{ color: 'var(--text-muted)', margin: 0 }}>No faculty registered under this university.</p>
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '12px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                        <thead>
                                            <tr style={{ background: 'var(--bg-body)', borderBottom: '2px solid var(--border)' }}>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Faculty Member</th>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Department</th>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Designation</th>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {associatedUsers.faculty.map(fac => (
                                                <tr key={fac.id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.2s' }}>
                                                    <td style={{ padding: '16px 18px' }}>
                                                        <div style={{ fontWeight: 600, color: 'var(--proto-white)' }}>{fac.full_name || 'Unnamed Faculty'}</div>
                                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{fac.email}</div>
                                                    </td>
                                                    <td style={{ padding: '16px 18px', color: 'var(--proto-white)' }}>{fac.department || '—'}</td>
                                                    <td style={{ padding: '16px 18px', color: 'var(--text-muted)' }}>{fac.designation || '—'}</td>
                                                    <td style={{ padding: '16px 18px' }}>
                                                        <span className={`badge ${fac.is_active ? 'active' : 'inactive'}`} style={{ padding: '4px 10px', fontSize: '11px' }}>
                                                            {fac.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'students' && (
                        <div className="info-section">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <h2>Registered Students</h2>
                                <span className="badge verified">{associatedUsers.students.length} Enrolled</span>
                            </div>
                            {loadingUsers ? (
                                <div style={{ textAlign: 'center', padding: '2rem' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
                            ) : associatedUsers.students.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '3rem', border: '1px dashed var(--border)', borderRadius: '12px', background: 'rgba(255,255,255,0.02)' }}>
                                    <Users size={40} style={{ color: 'var(--text-muted)', marginBottom: '1rem' }} />
                                    <p style={{ color: 'var(--text-muted)', margin: 0 }}>No students registered under this university.</p>
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '12px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                        <thead>
                                            <tr style={{ background: 'var(--bg-body)', borderBottom: '2px solid var(--border)' }}>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Student</th>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>USN</th>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Department</th>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Batch</th>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {associatedUsers.students.map(stu => (
                                                <tr key={stu.id} onClick={() => setSelectedStudent(stu)} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.2s', cursor: 'pointer' }}>
                                                    <td style={{ padding: '16px 18px' }}>
                                                        <div style={{ fontWeight: 600, color: 'var(--proto-white)' }}>{stu.full_name || 'Unnamed Student'}</div>
                                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{stu.email || '—'}</div>
                                                    </td>
                                                    <td style={{ padding: '16px 18px', color: 'var(--proto-white)', fontFamily: 'monospace' }}>{stu.usn || '—'}</td>
                                                    <td style={{ padding: '16px 18px', color: 'var(--proto-white)' }}>{stu.department || '—'}</td>
                                                    <td style={{ padding: '16px 18px', color: 'var(--text-muted)' }}>{stu.academic_batch || '—'}</td>
                                                    <td style={{ padding: '16px 18px' }}>
                                                        <span className={`badge ${stu.is_active ? 'active' : 'inactive'}`} style={{ padding: '4px 10px', fontSize: '11px' }}>
                                                            {stu.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'ai_tiers' && (
                        <div className="info-section">
                            <StageAllocation universityId={id} institutionName={university?.institution_name} />
                        </div>
                    )}

                    {activeTab === 'tpo' && (
                        <div className="info-section">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <h2>Registered TPO Officers</h2>
                                <span className="badge verified">{(associatedUsers.tpo || []).length} Officers</span>
                            </div>
                            {loadingUsers ? (
                                <div style={{ textAlign: 'center', padding: '2rem' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
                            ) : (associatedUsers.tpo || []).length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '3rem', border: '1px dashed var(--border)', borderRadius: '12px', background: 'rgba(255,255,255,0.02)' }}>
                                    <Briefcase size={40} style={{ color: 'var(--text-muted)', marginBottom: '1rem' }} />
                                    <p style={{ color: 'var(--text-muted)', margin: 0 }}>No TPO Officers registered under this university.</p>
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '12px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                        <thead>
                                            <tr style={{ background: 'var(--bg-body)', borderBottom: '2px solid var(--border)' }}>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Officer</th>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>TPO ID</th>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Department</th>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Phone</th>
                                                <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(associatedUsers.tpo || []).map(tp => (
                                                <tr key={tp.id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.2s' }}>
                                                    <td style={{ padding: '16px 18px' }}>
                                                        <div style={{ fontWeight: 600, color: 'var(--proto-white)' }}>{tp.full_name || 'Unnamed Officer'}</div>
                                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{tp.email || '—'}</div>
                                                    </td>
                                                    <td style={{ padding: '16px 18px', color: 'var(--proto-white)', fontFamily: 'monospace' }}>{tp.tpo_id || '—'}</td>
                                                    <td style={{ padding: '16px 18px', color: 'var(--proto-white)' }}>{tp.department || '—'}</td>
                                                    <td style={{ padding: '16px 18px', color: 'var(--text-muted)' }}>{tp.phone || '—'}</td>
                                                    <td style={{ padding: '16px 18px' }}>
                                                        <span className={`badge ${tp.is_active ? 'active' : 'inactive'}`} style={{ padding: '4px 10px', fontSize: '11px' }}>
                                                            {tp.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'roles' && (() => {
                        const customRoleUsers = (associatedUsers.roles || []).flatMap(role => 
                            (role.users || []).map(u => ({ ...u, roleName: role.name, roleDescription: role.description }))
                        );
                        
                        return (
                            <div className="info-section">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        <div style={{ width: '4px', height: '24px', background: 'var(--primary)', borderRadius: '4px' }}></div>
                                        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Custom Role Users</h2>
                                    </div>
                                    <span style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', background: 'rgba(0, 229, 255, 0.1)', color: 'var(--primary)', borderRadius: '100px' }}>
                                        {customRoleUsers.length} Users
                                    </span>
                                </div>
                                
                                {loadingUsers ? (
                                    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                                        <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
                                        <p>Loading custom roles...</p>
                                    </div>
                                ) : customRoleUsers.length === 0 ? (
                                    <div style={{ padding: '60px 20px', textAlign: 'center', background: 'var(--bg-card)', borderRadius: '12px', border: '1px dashed var(--border)' }}>
                                        <Shield size={48} style={{ color: 'var(--border-strong)', margin: '0 auto 16px', opacity: 0.5 }} />
                                        <h3 style={{ color: 'var(--text-color)', marginBottom: '8px', fontSize: '1.2rem' }}>No Custom Role Users Found</h3>
                                        <p style={{ color: 'var(--text-muted)' }}>This university has not assigned any users to custom roles yet.</p>
                                    </div>
                                ) : (
                                    <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '12px' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                            <thead>
                                                <tr style={{ background: 'var(--bg-body)', borderBottom: '2px solid var(--border)' }}>
                                                    <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>User</th>
                                                    <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Role Name</th>
                                                    <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Description</th>
                                                    <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Phone</th>
                                                    <th style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 800 }}>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {customRoleUsers.map((user, idx) => (
                                                    <tr key={idx} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.2s' }}>
                                                        <td style={{ padding: '16px 18px' }}>
                                                            <div style={{ fontWeight: 600, color: 'var(--proto-white)' }}>{user.name || 'Unnamed User'}</div>
                                                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{user.email || '—'}</div>
                                                        </td>
                                                        <td style={{ padding: '16px 18px', color: 'var(--proto-white)' }}>{user.roleName || '—'}</td>
                                                        <td style={{ padding: '16px 18px', color: 'var(--proto-white)' }}>{user.roleDescription || '—'}</td>
                                                        <td style={{ padding: '16px 18px', color: 'var(--text-muted)' }}>{user.phone || '—'}</td>
                                                        <td style={{ padding: '16px 18px' }}>
                                                            <span style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '100px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                                                                ACTIVE
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Deactivate Confirmation Modal */}
            {showDeleteModal && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal">
                        <div className="purge-icon-wrapper">
                            <Trash2 size={32} />
                        </div>
                        <h2>Deactivate University Account</h2>
                        <p className="text-muted">
                            Deactivating this university will disable access for all associated Students, Faculty, and TPO accounts. No data will be deleted. Do you want to continue?
                        </p>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>
                                Cancel
                            </button>
                            <button className="confirm-btn danger" onClick={handleDeactivateAccount}>
                                Deactivate Account
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Student Detail Modal */}
            {selectedStudent && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal" style={{ maxWidth: '600px', padding: '2rem', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                        <button className="admin-close-btn" onClick={() => setSelectedStudent(null)}><XCircle size={24} /></button>
                        
                        <div className="modal-content-header" style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
                            <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{selectedStudent.full_name || 'Unnamed Student'}</h2>
                            <span className="badge active" style={{ display: 'inline-block' }}>Student</span>
                        </div>

                        <div className="modal-body" style={{ textAlign: 'left', fontSize: '0.95rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem 1.5rem', marginBottom: '1.5rem' }}>
                                <div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px', textTransform: 'uppercase', fontWeight: 600 }}>Email</div>
                                    <div style={{ fontWeight: 500, color: 'var(--proto-white)' }}>{selectedStudent.email || '—'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px', textTransform: 'uppercase', fontWeight: 600 }}>USN</div>
                                    <div style={{ fontWeight: 500, color: 'var(--proto-white)' }}>{selectedStudent.usn || '—'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px', textTransform: 'uppercase', fontWeight: 600 }}>Gender</div>
                                    <div style={{ fontWeight: 500, color: 'var(--proto-white)' }}>{selectedStudent.gender || '—'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px', textTransform: 'uppercase', fontWeight: 600 }}>Batch</div>
                                    <div style={{ fontWeight: 500, color: 'var(--proto-white)' }}>{selectedStudent.academic_batch || '—'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px', textTransform: 'uppercase', fontWeight: 600 }}>Department</div>
                                    <div style={{ fontWeight: 500, color: 'var(--proto-white)' }}>{selectedStudent.department || '—'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px', textTransform: 'uppercase', fontWeight: 600 }}>Degree</div>
                                    <div style={{ fontWeight: 500, color: 'var(--proto-white)' }}>{selectedStudent.degree || '—'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px', textTransform: 'uppercase', fontWeight: 600 }}>Year of Admission</div>
                                    <div style={{ fontWeight: 500, color: 'var(--proto-white)' }}>{selectedStudent.year_of_admission || '—'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px', textTransform: 'uppercase', fontWeight: 600 }}>Graduation Year</div>
                                    <div style={{ fontWeight: 500, color: 'var(--proto-white)' }}>{selectedStudent.graduation_year || '—'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px', textTransform: 'uppercase', fontWeight: 600 }}>Domain</div>
                                    <div style={{ fontWeight: 500, color: 'var(--proto-white)' }}>{selectedStudent.primary_domain || '—'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px', textTransform: 'uppercase', fontWeight: 600 }}>Violations</div>
                                    <div style={{ fontWeight: 600, color: selectedStudent.violation_count > 0 ? '#dc2626' : 'var(--proto-white)' }}>
                                        {selectedStudent.violation_count || 0}
                                    </div>
                                </div>
                            </div>
                            
                            {(selectedStudent.skills || selectedStudent.admission_number || selectedStudent.academic_year) && (
                                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.5rem', marginBottom: '1.5rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem 1.5rem' }}>
                                        {selectedStudent.admission_number && (
                                            <div>
                                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px', textTransform: 'uppercase', fontWeight: 600 }}>Admission No</div>
                                                <div style={{ fontWeight: 500, color: 'var(--proto-white)' }}>{selectedStudent.admission_number}</div>
                                            </div>
                                        )}
                                        {selectedStudent.academic_year && (
                                            <div>
                                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px', textTransform: 'uppercase', fontWeight: 600 }}>Academic Year</div>
                                                <div style={{ fontWeight: 500, color: 'var(--proto-white)' }}>{selectedStudent.academic_year}</div>
                                            </div>
                                        )}
                                    </div>
                                    {selectedStudent.skills && (
                                        <div style={{ marginTop: '1rem' }}>
                                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>Skills</div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                {selectedStudent.skills.split(',').map((skill, idx) => (
                                                    <span key={idx} style={{ background: 'var(--border)', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', color: 'var(--proto-white)', fontWeight: 500 }}>{skill.trim()}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--border)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <strong style={{ color: 'var(--proto-white)', fontSize: '14px' }}>Account Status</strong>
                                <span className={`badge ${selectedStudent.is_active ? 'active' : 'inactive'}`} style={{ padding: '6px 12px' }}>
                                    {selectedStudent.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                        
                        <div className="modal-actions" style={{ justifyContent: 'flex-end', marginTop: '2rem' }}>
                            <button onClick={() => setSelectedStudent(null)} style={{ padding: '8px 24px', background: 'var(--border)', color: 'var(--proto-white)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UniversityProfileView;

